import { formatAuthorName } from 'utils/formatters';
import { DateFormatter } from 'infrastructure/functions';
import { DateFormats } from 'infrastructure/enums';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { type FC } from 'react';
import { DOCUMENTATION_TIME_PLACEHOLDER } from 'infrastructure/consts/tables';
import { convertSecondsToTime } from 'utils/timeHelper';
import { Flex, Tag } from 'antd';
import BaseDropdownButton from 'components/atoms/baseButton/dropdown';
import { EllipsisOutlined } from '@ant-design/icons';

import s from './styles.module.scss';
import { MedicationInfo } from './info';

import type {
  IMedicationForUpdate,
  IMedication,
} from 'infrastructure/interfaces';

interface MedicationItemProps {
  item: IMedication;
  disableActions: boolean;
  onDelete?: (guid: string) => void;
  onEdit?: (item: IMedicationForUpdate) => void;
  loading: boolean;
  modifyItemGuid?: string;
  showActions?: boolean;
  flat?: boolean;
  showNote?: boolean;
}

const MedicationItem: FC<MedicationItemProps> = ({
  item,
  disableActions,
  loading,
  modifyItemGuid,
  onDelete,
  onEdit,
  showActions = true,
  flat = false,
  showNote = true,
}) => {
  const { t } = useTranslation();

  const { firstName, lastName, title } = item.updatedBy ?? item.author;
  const authorName = formatAuthorName({
    firstName,
    lastName,
    professionType: '',
    title,
  });
  const lastEditDate = DateFormatter({
    date: item.updatedAt,
    format: DateFormats['MM/dd/yyyy'],
  });

  const dropdownInfo = (
    <p className={s['dropdown-info']}>
      <span>
        {t('labels.lastEditedBy')}: {authorName},
      </span>
      <span>{lastEditDate}</span>
    </p>
  );

  const handleEdit = () => {
    if (!onEdit) return;

    const dosageMatch = item.dosage?.match(/^(\d+(?:\.\d+)?)\s*(.*)$/);
    const dosageValue = dosageMatch?.[1] ?? item.dosage ?? '';
    const unitValue = dosageMatch?.[2] ?? '';

    onEdit({
      dosage: dosageValue,
      unit: unitValue,
      duration: item.duration,
      endDate: item.endDate ? dayjs(item.endDate).toDate() : undefined,
      startDate: item.startDate ? dayjs(item.startDate).toDate() : undefined,
      frequency: item.frequency ?? '',
      name: item.name,
      isActive: item.isActive,
      note: item.note ?? '',
      route: item.route ?? '',
      guid: item.guid,
    });
  };

  const actionTime = !item?.documentationTime
    ? DOCUMENTATION_TIME_PLACEHOLDER
    : convertSecondsToTime(item?.documentationTime);

  return (
    <Flex
      vertical
      className={`${s.wrapper}${flat ? ` ${s.flat}` : ''}`}
      data-cy="medication-item"
    >
      <div className={s.header}>
        <div className={s['header-left']} data-cy="medication-name">
          <h5 className={s.title}>{item.name} </h5>
          <Tag
            color={item.isActive ? 'success' : 'default'}
            data-cy="medication-status"
            className={s.tag}
          >
            {item.isActive ? t('labels.active') : t('labels.inactive')}
          </Tag>
        </div>
        <div className={s['header-right']}>
          <p className={s['documentation-time']}>
            {t('labels.documentationTime')}: {actionTime}
          </p>
          {showActions && (
            <BaseDropdownButton
              type="secondary"
              loading={loading && item.guid === modifyItemGuid}
              disabled={disableActions}
              className={s['dropdown-btn']}
              icon={<EllipsisOutlined />}
              items={[
                {
                  key: 'edit',
                  label: t('labels.edit'),
                  onClick: handleEdit,
                },
                {
                  key: 'delete',
                  label: t('labels.delete'),
                  className: s.danger,
                  onClick: () => onDelete?.(item.guid),
                },
                {
                  key: 'info',
                  label: dropdownInfo,
                  disabled: true,
                },
              ]}
            />
          )}
        </div>
      </div>
      <div className={s.body} data-cy="medication-description">
        <MedicationInfo item={item} showNote={showNote} />
      </div>
    </Flex>
  );
};
export default MedicationItem;
